// Browser-side video toolbox for AI Studio, built on ffmpeg.wasm.
// Single-thread core — no SharedArrayBuffer / COOP-COEP headers needed, so it
// runs on any host. Loaded once and reused; all work stays in the browser and
// puts zero load on the server.

const FFMPEG_CORE_VERSION = '0.12.6';
const CORE_BASE = `https://unpkg.com/@ffmpeg/core@${FFMPEG_CORE_VERSION}/dist/umd`;

type FFmpeg = import('@ffmpeg/ffmpeg').FFmpeg;

let instance: FFmpeg | null = null;
let progressCb: ((pct: number) => void) | null = null;

export function onFfmpegProgress(cb: ((pct: number) => void) | null) {
  progressCb = cb;
}

async function getFFmpeg(): Promise<FFmpeg> {
  if (instance) return instance;
  const { FFmpeg } = await import('@ffmpeg/ffmpeg');
  const { toBlobURL } = await import('@ffmpeg/util');
  const ffmpeg = new FFmpeg();
  ffmpeg.on('progress', ({ progress }) => progressCb?.(Math.min(99, Math.round(progress * 100))));
  await ffmpeg.load({
    coreURL: await toBlobURL(`${CORE_BASE}/ffmpeg-core.js`, 'text/javascript'),
    wasmURL: await toBlobURL(`${CORE_BASE}/ffmpeg-core.wasm`, 'application/wasm'),
  });
  instance = ffmpeg;
  return ffmpeg;
}

async function writeInput(ffmpeg: FFmpeg, name: string, url: string) {
  const { fetchFile } = await import('@ffmpeg/util');
  await ffmpeg.writeFile(name, await fetchFile(url));
}

async function readOutput(ffmpeg: FFmpeg, name: string, mime: string): Promise<Blob> {
  const data = await ffmpeg.readFile(name);
  await ffmpeg.deleteFile(name).catch(() => undefined);
  return new Blob([data as Uint8Array], { type: mime });
}

/** Replace a video's own audio track with a voiceover. `-shortest` trims to the shorter input. */
export async function mergeVideoAudio(videoUrl: string, audioUrl: string): Promise<Blob> {
  const ffmpeg = await getFFmpeg();
  await writeInput(ffmpeg, 'in.mp4', videoUrl);
  await writeInput(ffmpeg, 'voice.mp3', audioUrl);
  await ffmpeg.exec([
    '-i', 'in.mp4', '-i', 'voice.mp3',
    '-map', '0:v:0', '-map', '1:a:0',
    '-c:v', 'copy', '-c:a', 'aac', '-shortest', 'out.mp4',
  ]);
  return readOutput(ffmpeg, 'out.mp4', 'video/mp4');
}

/**
 * Join clips back-to-back into one video. Tries the stream-copy concat first
 * (instant when every clip shares codec parameters — true for Veo outputs);
 * falls back to a re-encode when they don't, which is slower but always works.
 */
export async function concatClips(urls: string[]): Promise<Blob> {
  if (urls.length < 2) throw new Error('Need at least two clips to join');
  const ffmpeg = await getFFmpeg();

  const names: string[] = [];
  for (let i = 0; i < urls.length; i++) {
    const name = `clip${i}.mp4`;
    await writeInput(ffmpeg, name, urls[i]);
    names.push(name);
  }
  await ffmpeg.writeFile('list.txt', names.map((n) => `file '${n}'`).join('\n'));

  try {
    await ffmpeg.exec(['-f', 'concat', '-safe', '0', '-i', 'list.txt', '-c', 'copy', 'joined.mp4']);
  } catch {
    // Mixed codec parameters (e.g. an uploaded clip next to a Veo one) —
    // re-encode. ultrafast keeps browser time tolerable at social resolutions.
    const inputs = names.flatMap((n) => ['-i', n]);
    const filter = names.map((_, i) => `[${i}:v:0]`).join('') + `concat=n=${names.length}:v=1[v]`;
    await ffmpeg.exec([...inputs, '-filter_complex', filter, '-map', '[v]',
      '-c:v', 'libx264', '-preset', 'ultrafast', '-pix_fmt', 'yuv420p', 'joined.mp4']);
  }
  return readOutput(ffmpeg, 'joined.mp4', 'video/mp4');
}

/**
 * Grab the last frame of a video as a JPEG — the seed for the next 8s Veo
 * clip, so consecutive clips flow into each other ("continue from" chaining).
 * ffmpeg rather than <canvas> because canvas taints on cross-origin media.
 */
export async function extractLastFrame(videoUrl: string): Promise<Blob> {
  const ffmpeg = await getFFmpeg();
  await writeInput(ffmpeg, 'src.mp4', videoUrl);
  // -sseof must precede -i: seek to a quarter-second before the end.
  await ffmpeg.exec(['-sseof', '-0.25', '-i', 'src.mp4', '-frames:v', '1', '-q:v', '2', 'frame.jpg']);
  return readOutput(ffmpeg, 'frame.jpg', 'image/jpeg');
}
